import { useState } from "react";
import { Star } from "lucide-react";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import { REVIEWS } from "../data/reviews";

const PAGE_SIZE = 21;
const GOOGLE_REVIEWS_URL = "https://www.google.com/search?q=lime+driving+school+southampton+google+reviews";

export default function Reviews() {
  const [visible, setVisible] = useState(PAGE_SIZE);
  const shown = REVIEWS.slice(0, visible);
  const remaining = REVIEWS.length - visible;

  const schema = {
    "@context": "https://schema.org",
    "@type": "DrivingSchool",
    "name": "Lime Driving School",
    "review": REVIEWS.map(r => ({
      "@type": "Review",
      "author": { "@type": "Person", "name": r.name },
      "reviewBody": r.text,
      "reviewRating": { "@type": "Rating", "ratingValue": 5, "bestRating": 5 },
    })),
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": 5, "reviewCount": REVIEWS.length },
  };

  return (
    <>
      <Seo title="Reviews — Lime Driving School Southampton" description="95+ verified 5-star Google reviews from drivers who passed with Akram at Lime Driving School." jsonLd={schema} />
      <PageHero
        eyebrow="Reviews"
        title="The pass gallery"
        subtitle={`${REVIEWS.length} verified 5-star Google reviews from learners who passed their tests with Akram. Want to be next?`}
      />
      <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="reviews-page">
        <div className="flex justify-center mb-10">
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="see-all-google-reviews"
            className="inline-flex items-center gap-3 bg-white border border-black/10 hover:border-[#37B160] hover:shadow-md transition-all rounded-full pl-5 pr-6 py-3 group"
          >
            <span aria-hidden="true" className="grid place-items-center h-8 w-8 rounded-full bg-white border border-black/10 group-hover:border-[#37B160]/30 transition-colors">
              {/* Google "G" mark */}
              <svg viewBox="0 0 48 48" className="h-4 w-4">
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
              </svg>
            </span>
            <span className="text-sm font-medium text-[#111827]">
              See all 5★ Google reviews
              <span className="ml-2 text-[#9CA3AF] text-xs">↗</span>
            </span>
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reviews-grid">
          {shown.map((r, i) => (
            <article
              key={`${r.name}-${i}`}
              className="bg-white border border-black/5 rounded-2xl p-6 flex flex-col"
              data-testid={`review-${i}`}
            >
              <div className="flex gap-0.5 mb-3" aria-label="5 star rating">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="h-4 w-4 fill-[#37B160] text-[#37B160]" />
                ))}
              </div>
              <p className="text-sm text-[#4B5563] leading-relaxed flex-1">{r.text}</p>
              <footer className="mt-4 pt-4 border-t border-black/5 flex items-baseline justify-between gap-2">
                <span className="text-xs uppercase tracking-[0.2em] text-[#111827] font-semibold truncate">{r.name}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#9CA3AF] flex-none">{r.period}</span>
              </footer>
            </article>
          ))}
        </div>

        {remaining > 0 && (
          <div className="mt-12 flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => setVisible(v => v + PAGE_SIZE)}
              data-testid="show-more-reviews"
              className="text-sm font-medium text-[#37B160] hover:text-[#2D9450] underline underline-offset-[6px] decoration-2 decoration-[#37B160]/30 hover:decoration-[#37B160] transition-colors"
            >
              Show {Math.min(PAGE_SIZE, remaining)} more reviews
            </button>
            <span className="text-xs text-[#9CA3AF]">
              Showing {shown.length} of {REVIEWS.length}
            </span>
          </div>
        )}
        {remaining === 0 && REVIEWS.length > PAGE_SIZE && (
          <div className="mt-12 flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => { setVisible(PAGE_SIZE); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              data-testid="collapse-reviews"
              className="text-sm text-[#9CA3AF] hover:text-[#4B5563] underline underline-offset-[6px]"
            >
              Collapse to top 21
            </button>
            <span className="text-xs text-[#9CA3AF]">
              You've seen all {REVIEWS.length} reviews.
            </span>
          </div>
        )}
      </section>
    </>
  );
}
